import { useParams, Link } from 'react-router-dom';
import { allArticles, ArticleData } from '@/mocks/article-content';
import { SITE_URL } from '@/constants';

function buildBreadcrumbSchema(article: ArticleData) {
  const articleUrl = SITE_URL + '/article/' + article.slug;
  return JSON.stringify({
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Home",
        "item": SITE_URL
      },
      {
        "@type": "ListItem",
        "position": 2,
        "name": article.headline,
        "item": articleUrl
      }
    ]
  });
}

function buildArticleSchema(article: ArticleData) {
  const articleUrl = SITE_URL + '/article/' + article.slug;
  const schema: Record<string, unknown> = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": article.headline,
    "description": article.excerpt,
    "image": article.imageUrl,
    "datePublished": "2026-07-01",
    "dateModified": "2026-07-12",
    "author": {
      "@type": "Organization",
      "name": "Hipobuy Spreadsheet Hub",
      "url": SITE_URL
    },
    "publisher": {
      "@type": "Organization",
      "name": "Hipobuy Spreadsheet Hub",
      "url": SITE_URL
    },
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": articleUrl
    },
    "inLanguage": "en"
  };
  return JSON.stringify(schema);
}

function buildFAQSchema(article: ArticleData) {
  const faqSection = article.sections.find(s => s.type === 'faq');
  if (!faqSection || !faqSection.faqs || faqSection.faqs.length === 0) return null;
  const schema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqSection.faqs.map(faq => ({
      "@type": "Question",
      "name": faq.q,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.a
      }
    }))
  };
  return JSON.stringify(schema);
}

function SectionRenderer({ section }: { section: ArticleData['sections'][0] }) {
  switch (section.type) {
    case 'heading':
      return (
        <section className="mb-10">
          <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-4">
            {section.heading}
          </h2>
          {section.paragraphs?.map((p, i) => (
            <p key={i} className="text-foreground-700 leading-relaxed mb-4" dangerouslySetInnerHTML={{ __html: p }} />
          ))}
          {section.categoriesList && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-6">
              {section.categoriesList.map((cat) => (
                <div key={cat.name} className="p-4 rounded-xl bg-background-100 border border-background-200/40">
                  <div className="flex items-center justify-between mb-1">
                    <h4 className="font-semibold text-foreground-900 text-sm">{cat.name}</h4>
                    <span className="text-xs font-medium text-primary-600 bg-primary-50 px-2 py-0.5 rounded-full">{cat.count}</span>
                  </div>
                  <p className="text-foreground-500 text-xs leading-relaxed">{cat.desc}</p>
                </div>
              ))}
            </div>
          )}
          {section.steps && (
            <div className="space-y-5 mb-6">
              {section.steps.map((item) => (
                <div key={item.step} className="flex gap-4">
                  <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0">
                    <span className="text-primary-700 font-bold text-sm">{item.step}</span>
                  </div>
                  <div>
                    <h4 className="font-semibold text-foreground-900 text-base mb-1">{item.title}</h4>
                    <p className="text-foreground-600 text-sm leading-relaxed">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>
      );

    case 'table':
      return (
        <section className="mb-10">
          {section.heading && (
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-4">{section.heading}</h2>
          )}
          {section.paragraphs?.map((p, i) => (
            <p key={i} className="text-foreground-700 leading-relaxed mb-4" dangerouslySetInnerHTML={{ __html: p }} />
          ))}
          {section.table && (
            <div className="overflow-x-auto mb-6 rounded-xl border border-background-200/60">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-background-100">
                    {section.table.headers.map((h) => (
                      <th key={h} className="text-left p-4 font-semibold text-foreground-900">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {section.table.rows.map((row, ri) => (
                    <tr key={ri} className="border-t border-background-200/40">
                      {row.map((cell, ci) => (
                        <td key={ci} className={`p-4 ${ci === 0 ? 'font-medium text-foreground-900' : ci === row.length - 1 ? 'text-accent-700 font-medium' : 'text-foreground-600'}`}>{cell}</td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      );

    case 'faq':
      return (
        <section className="mb-10">
          <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-4">{section.heading}</h2>
          <div className="space-y-4">
            {section.faqs?.map((faq, idx) => (
              <details key={idx} className="group rounded-xl border border-background-200/60 overflow-hidden">
                <summary className="flex items-center justify-between p-4 cursor-pointer bg-background-50 hover:bg-background-100 transition-colors">
                  <span className="font-medium text-foreground-900 text-sm pr-4">{faq.q}</span>
                  <i className="ri-add-line text-foreground-500 group-open:hidden w-4 h-4 flex items-center justify-center flex-shrink-0"></i>
                  <i className="ri-subtract-line text-foreground-500 hidden group-open:block w-4 h-4 flex items-center justify-center flex-shrink-0"></i>
                </summary>
                <div className="px-4 pb-4">
                  <p className="text-foreground-600 text-sm leading-relaxed">{faq.a}</p>
                </div>
              </details>
            ))}
          </div>
        </section>
      );

    case 'cta':
      return (
        <section className="mb-10 p-6 md:p-8 rounded-2xl bg-accent-50 border border-accent-200/40">
          <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-3">
            Ready to Start Exploring?
          </h2>
          <p className="text-foreground-600 text-sm leading-relaxed mb-5">{section.ctaText}</p>
          <div className="flex flex-col sm:flex-row gap-3">
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="whitespace-nowrap inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full bg-primary-500 text-background-50 dark:text-foreground-950 text-sm font-semibold transition-all hover:bg-primary-600 hover:-translate-y-0.5 cursor-pointer"
            >
              {section.ctaPrimaryLabel || 'Visit Kaspreadsheet'}
              <i className="ri-arrow-right-up-line w-4 h-4 flex items-center justify-center"></i>
            </a>
            <Link
              to="/"
              className="whitespace-nowrap inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full bg-background-100 text-foreground-800 text-sm font-semibold transition-all hover:bg-background-200 cursor-pointer"
            >
              {section.ctaSecondaryLabel || 'Back to Home'}
              <i className="ri-arrow-left-line w-4 h-4 flex items-center justify-center"></i>
            </Link>
          </div>
        </section>
      );

    default:
      return null;
  }
}

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = allArticles.find((a) => a.slug === slug);
  const otherArticles = allArticles.filter((a) => a.slug !== slug);

  if (!article) {
    return (
      <main className="w-full min-h-screen bg-background-50 flex flex-col items-center justify-center py-20">
        <div className="text-center px-4">
          <div className="w-20 h-20 rounded-full bg-accent-100 flex items-center justify-center mx-auto mb-6">
            <i className="ri-file-unknow-line text-accent-600 text-3xl w-8 h-8 flex items-center justify-center"></i>
          </div>
          <h1 className="font-heading text-3xl font-bold text-foreground-950 mb-3">Article Not Found</h1>
          <p className="text-foreground-600 mb-6">The article you are looking for does not exist or has been moved.</p>
          <Link
            to="/"
            className="whitespace-nowrap inline-flex items-center gap-2 px-6 py-3 rounded-full bg-primary-500 text-background-50 dark:text-foreground-950 text-sm font-semibold cursor-pointer"
          >
            <i className="ri-home-line w-4 h-4 flex items-center justify-center"></i>
            Back to Home
          </Link>
        </div>
      </main>
    );
  }

  return (
    <main className="w-full min-h-screen bg-background-50">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: buildBreadcrumbSchema(article) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: buildArticleSchema(article) }}
      />
      {buildFAQSchema(article) && (
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: buildFAQSchema(article)! }}
        />
      )}
      <header className="w-full border-b border-background-200/60 bg-background-50/95 backdrop-blur-lg sticky top-0 z-40">
        <div className="w-full px-4 md:px-6 lg:px-10">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-2 text-foreground-900 font-bold text-lg font-heading cursor-pointer">
              <i className="ri-arrow-left-line w-5 h-5 flex items-center justify-center"></i>
              hipobuyspread<span className="text-primary-500">.baby</span>
            </Link>
            <Link
              to="/"
              className="whitespace-nowrap text-sm text-foreground-600 hover:text-foreground-900 transition-colors cursor-pointer"
            >
              Back to Home
            </Link>
          </div>
        </div>
      </header>

      <article className="w-full max-w-4xl mx-auto px-4 md:px-6 py-10 md:py-16">
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-4">
            <span className="text-xs font-medium text-accent-600 bg-accent-50 px-3 py-1 rounded-full">{article.category}</span>
            <span className="text-xs text-foreground-500">{article.readTime}</span>
            <span className="text-xs text-foreground-500">Updated July 2026</span>
          </div>
          <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-foreground-950 leading-tight mb-4">
            {article.title}
          </h1>
          <p className="text-lg text-foreground-600 leading-relaxed">
            {article.excerpt}
          </p>
        </div>

        <div className="w-full h-[300px] md:h-[400px] rounded-2xl overflow-hidden mb-10 bg-background-100">
          <img
            src={article.imageUrl}
            alt={article.headline}
            className="w-full h-full object-cover"
          />
        </div>

        <div>
          {article.sections.map((section, idx) => (
            <SectionRenderer key={idx} section={section} />
          ))}

          <div className="flex flex-wrap items-center gap-2 pt-6 border-t border-background-200/60">
            <span className="text-xs text-foreground-500">Related Keywords:</span>
            {article.relatedKeywords.map((kw) => (
              <span key={kw} className="px-2.5 py-1 rounded-full bg-background-100 text-foreground-600 text-xs border border-background-200/40">
                {kw}
              </span>
            ))}
          </div>
        </div>

        {otherArticles.length > 0 && (
          <div className="mt-14 pt-10 border-t border-background-200/60">
            <h3 className="font-heading text-xl font-bold text-foreground-950 mb-5">Read More Comparisons</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {otherArticles.slice(0, 4).map((a) => (
                <Link
                  key={a.slug}
                  to={`/article/${a.slug}`}
                  className="group flex items-start gap-4 p-4 rounded-xl bg-background-100 border border-background-200/40 hover:border-accent-200/60 transition-all cursor-pointer"
                >
                  <div className="w-20 h-20 rounded-lg overflow-hidden flex-shrink-0 bg-background-200/50">
                    <img src={a.imageUrl} alt={a.headline} className="w-full h-full object-cover" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <span className="text-xs font-medium text-accent-600">{a.category}</span>
                    <h4 className="font-semibold text-foreground-900 text-sm mt-1 leading-snug group-hover:text-primary-600 transition-colors line-clamp-2">
                      {a.headline}
                    </h4>
                    <p className="text-foreground-500 text-xs mt-1">{a.readTime}</p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}
      </article>

      <footer className="w-full bg-foreground-950 text-background-50 py-10">
        <div className="w-full px-4 md:px-6 lg:px-10 text-center">
          <p className="text-xs text-background-50/40 mb-2">
            This website is an independent educational resource. We are not affiliated with any shopping agent platform.
          </p>
          <Link to="/" className="text-sm text-background-50/60 hover:text-background-50/80 transition-colors cursor-pointer">
            &larr; Back to Homepage
          </Link>
        </div>
      </footer>
    </main>
  );
}